import type { InternalAxiosRequestConfig } from 'axios';
import ENVIRONMENT from '../../../environment/environment';
import request from '../base-service';
import type { InfoTarget } from '@/types/performance/edtichitieuviewv2';
import type { RequestSelectTree } from '@/types/base';
import {
  COPY_DATA_STEP_LABELS,
  COPY_DATA_STEP_ORDER,
  type CopyDataSignalPayload,
  type CopyDataSignalPhase,
  type CopyDataStepKey,
  type CopyDataStepState,
  type CopyDataStepStatus,
} from '@/types/copy-data-progress';

const tenantApiBaseURL = (
  process.env.NEXT_PUBLIC_TENANT_API_BASE_URL ||
  process.env.CONFIG_NEXT_PUBLIC_TENANT_API_BASE_URL ||
  ''
).trim().replace(/\/$/, '');

export const withTenantApiBase = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => ({
  ...config,
  ...(tenantApiBaseURL ? { baseURL: tenantApiBaseURL } : {}),
});

export interface RequestCopyDataFromDashboard {
  tuNam: number;
  denNam: number;
  isBaoCaoKPIs: boolean;
  isTiTrong: boolean;
  isActionPlan: boolean;
  isIPOO: boolean;
  isKPIChung: boolean;
  isBaoCaoKPIChung: boolean;
}

export interface CopyDataFromDashboardResponse {
  success?: boolean;
  msg?: string;
  message?: string;
  jobId?: string;
  runId?: string;
  data?: {
    jobId?: string;
    runId?: string;
    success?: boolean;
    msg?: string;
    message?: string;
  };
}

export interface RequestCopyDataStatus {
  jobId: string;
}

export type {
  CopyDataStepKey,
  CopyDataStepStatus,
  CopyDataSignalPayload,
  CopyDataStepState,
};

const STEP_KEY_ALIASES: Record<string, CopyDataStepKey> = {
  muctieuxuongcakpis: 'mucTieuXuongCaKpis',
  muc_tieu_xuong_ca_kpis: 'mucTieuXuongCaKpis',
  baocaokpis: 'baoCaoKpis',
  bao_cao_kpis: 'baoCaoKpis',
  isbaocaokpis: 'baoCaoKpis',
  titrong: 'tiTrong',
  ti_trong: 'tiTrong',
  istittrong: 'tiTrong',
  actionplan: 'actionPlan',
  action_plan: 'actionPlan',
  isactionplan: 'actionPlan',
  ipoo: 'ipoo',
  isipoo: 'ipoo',
  kpichung: 'kpiChung',
  kpi_chung: 'kpiChung',
  iskpichung: 'kpiChung',
  baocaokpichung: 'baoCaoKpiChung',
  bao_cao_kpi_chung: 'baoCaoKpiChung',
  isbaocaokpichung: 'baoCaoKpiChung',
};

const STEP_STATUS_ALIASES: Record<string, CopyDataStepStatus> = {
  pending: 'pending',
  waiting: 'pending',
  running: 'running',
  inprogress: 'running',
  'in-progress': 'running',
  done: 'done',
  completed: 'done',
  complete: 'done',
  success: 'done',
  failed: 'failed',
  error: 'failed',
  fail: 'failed',
  skipped: 'skipped',
  skip: 'skipped',
};

export function normalizeCopyDataStepKey(raw: unknown): CopyDataStepKey | undefined {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  if ((COPY_DATA_STEP_ORDER as string[]).includes(s)) return s as CopyDataStepKey;
  const alias = STEP_KEY_ALIASES[s.toLowerCase().replace(/\s+/g, '')];
  return alias;
}

export function normalizeCopyDataStepStatus(raw: unknown): CopyDataStepStatus | undefined {
  if (raw == null) return undefined;
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, '');
  return STEP_STATUS_ALIASES[s];
}

export function extractCopyDataJobId(response: unknown): string | null {
  if (!response || typeof response !== 'object') return null;
  const root = response as Record<string, unknown>;
  const data =
    root.data && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>)
      : undefined;

  const candidates = [
    root.jobId,
    root.JobId,
    root.runId,
    root.RunId,
    data?.jobId,
    data?.JobId,
    data?.runId,
    data?.RunId,
  ];

  for (const c of candidates) {
    if (c != null && String(c).trim()) return String(c).trim();
  }
  return null;
}

export function buildCopyDataStepsFromPayload(
  payload: RequestCopyDataFromDashboard
): CopyDataStepState[] {
  const selected = new Set<CopyDataStepKey>(['mucTieuXuongCaKpis']);
  if (payload.isBaoCaoKPIs) selected.add('baoCaoKpis');
  if (payload.isTiTrong) selected.add('tiTrong');
  if (payload.isActionPlan) selected.add('actionPlan');
  if (payload.isIPOO) selected.add('ipoo');
  if (payload.isKPIChung) selected.add('kpiChung');
  if (payload.isBaoCaoKPIChung) selected.add('baoCaoKpiChung');

  return COPY_DATA_STEP_ORDER.filter((key) => selected.has(key)).map((stepKey) => ({
    stepKey,
    label: COPY_DATA_STEP_LABELS[stepKey],
    status: 'pending' as const,
  }));
}

export function parseCopyDataSignal(notification: Record<string, unknown>): CopyDataSignalPayload | null {
  const data = (notification.data ?? notification.Data ?? {}) as Record<string, unknown>;
  const title = String(notification.title ?? notification.Title ?? '').toLowerCase();
  const body = String(notification.body ?? notification.Body ?? '').toLowerCase();
  const eventName = String(
    notification.eventName ?? notification.EventName ?? notification.type ?? notification.Type ?? ''
  ).toLowerCase();

  const phaseRaw = String(data.phase ?? data.Phase ?? body ?? '').toLowerCase();
  const isCopyEvent =
    eventName.includes('copydata') ||
    eventName.includes('copy-data') ||
    title.includes('copy data') ||
    title.includes('copy dữ liệu') ||
    title.includes('chuyển dữ liệu') ||
    title.includes('chuyen du lieu') ||
    Boolean(data.stepKey ?? data.StepKey) ||
    Boolean(data.jobId ?? data.JobId ?? data.runId ?? data.RunId);

  if (!isCopyEvent && !['start', 'progress', 'complete', 'failed'].includes(phaseRaw)) {
    return null;
  }

  // Avoid mistaking pure DB restore notifications that only have phase start/complete/progress
  if (
    !isCopyEvent &&
    (title.includes('database restore') || title.includes('db restore'))
  ) {
    return null;
  }

  let phase: CopyDataSignalPhase | undefined;
  if (phaseRaw === 'start') phase = 'start';
  else if (phaseRaw === 'progress') phase = 'progress';
  else if (phaseRaw === 'complete' || phaseRaw === 'completed') phase = 'complete';
  else if (phaseRaw === 'failed' || phaseRaw === 'fail' || phaseRaw === 'error') phase = 'failed';

  if (!phase && isCopyEvent) phase = 'progress';
  if (!phase) return null;

  const stepsRaw = data.steps ?? data.Steps;
  const steps = Array.isArray(stepsRaw)
    ? stepsRaw
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const row = item as Record<string, unknown>;
          const stepKey = normalizeCopyDataStepKey(row.stepKey ?? row.StepKey);
          const status = normalizeCopyDataStepStatus(row.status ?? row.Status ?? row.stepStatus ?? row.StepStatus);
          if (!stepKey || !status) return null;
          return { stepKey, status };
        })
        .filter(Boolean) as Array<{ stepKey: string; status: string }>
    : undefined;

  const percentRaw = data.percent ?? data.Percent;
  const percent =
    typeof percentRaw === 'number'
      ? percentRaw
      : typeof percentRaw === 'string' && percentRaw.trim()
        ? Number(percentRaw)
        : undefined;

  return {
    phase,
    jobId: extractCopyDataJobId({ ...notification, data }) ?? undefined,
    tuNam: toNumberOrNull(data.tuNam ?? data.TuNam) ?? undefined,
    denNam: toNumberOrNull(data.denNam ?? data.DenNam) ?? undefined,
    stepKey: normalizeCopyDataStepKey(data.stepKey ?? data.StepKey),
    stepStatus: normalizeCopyDataStepStatus(data.stepStatus ?? data.StepStatus ?? data.status ?? data.Status),
    percent: Number.isFinite(percent) ? Math.min(100, Math.max(0, percent as number)) : undefined,
    message: toStringOrNull(data.message ?? data.Message ?? data.msg ?? data.Msg) ?? undefined,
    steps,
  };
}

export function isCopyDataNotification(notification: Record<string, unknown>): boolean {
  const parsed = parseCopyDataSignal(notification);
  if (!parsed) return false;
  const title = String(notification.title ?? notification.Title ?? '').toLowerCase();
  const eventName = String(
    notification.eventName ?? notification.EventName ?? notification.type ?? notification.Type ?? ''
  ).toLowerCase();
  const data = (notification.data ?? notification.Data ?? {}) as Record<string, unknown>;
  return (
    eventName.includes('copydata') ||
    eventName.includes('copy-data') ||
    title.includes('copy') ||
    title.includes('chuyển dữ liệu') ||
    title.includes('chuyen du lieu') ||
    Boolean(data.stepKey ?? data.StepKey) ||
    Boolean(data.jobId ?? data.JobId ?? data.runId ?? data.RunId)
  );
}

export interface GetCopyDataTargetSetupsParams {
  ownerID: string;
  destinationYear: number;
}

/** Body POST /v1/performance/gettargetdetailhistory */
export interface GetTargetDetailHistoryParams {
  /** ID chỉ tiêu */
  id: string;
  /** Năm được chọn */
  selectNam: number;
  /** Phân hệ (vd: bsc) */
  phanHe: string;
  selectTree: RequestSelectTree;
}

/** Item từ copydata/target-setups — InfoTarget + năm/id nguồn để gettargetdetailhistory. */
export type CopyDataTargetSetupItem = InfoTarget & {
  sourceYear?: number | null;
  SourceYear?: number | null;
  sourceTargetID?: string | null;
  SourceTargetID?: string | null;
  id?: string | null;
};

function unwrapDataPayload(value: unknown): unknown {
  if (value && typeof value === 'object' && 'data' in value) {
    return (value as { data: unknown }).data;
  }
  return value;
}

function toNumberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toStringOrNull(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s || null;
}

export function extractCopyDataTargetSetups(response: unknown): CopyDataTargetSetupItem[] {
  const payload = unwrapDataPayload(unwrapDataPayload(response));

  if (Array.isArray(payload)) {
    return payload.filter(Boolean) as CopyDataTargetSetupItem[];
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const rootSourceYear =
      toNumberOrNull(record.sourceYear) ?? toNumberOrNull(record.SourceYear);
    const rootSourceTargetId =
      toStringOrNull(record.sourceTargetID) ?? toStringOrNull(record.SourceTargetID);

    // sourceYear có thể dùng chung cả năm; sourceTargetID là theo từng KPI — không gắn root ID cho mọi item.
    const attachRootSourceYear = (items: CopyDataTargetSetupItem[]) =>
      items.map((item) => {
        if (getCopyDataTargetSourceYear(item) != null || rootSourceYear == null) return item;
        return { ...item, sourceYear: rootSourceYear };
      });

    const candidateArrays = [
      record.listInfoTarget,
      record.items,
      record.targets,
      record.targetSetups,
      record.list,
      record.data,
    ];
    for (const candidate of candidateArrays) {
      if (Array.isArray(candidate)) {
        return attachRootSourceYear(candidate as CopyDataTargetSetupItem[]);
      }
    }

    if (record.chiTieu || record.chiTieuView || record.tenKPI || record.id || record.Id) {
      return attachRootSourceYear([payload as CopyDataTargetSetupItem]);
    }

    // Chỉ có metadata ở root (không list item) — không đủ để map theo KPI cụ thể
    if (rootSourceYear != null || rootSourceTargetId != null) {
      return [
        {
          ...(rootSourceYear != null ? { sourceYear: rootSourceYear } : {}),
          ...(rootSourceTargetId != null ? { sourceTargetID: rootSourceTargetId } : {}),
        } as CopyDataTargetSetupItem,
      ];
    }
  }

  return [];
}

export function getCopyDataTargetKpiKey(item: CopyDataTargetSetupItem): string {
  const anyItem = item as CopyDataTargetSetupItem & {
    idKpi?: string | null;
    idkpi?: string | null;
    IDKPI?: string | null;
  };
  return String(
    item.chiTieu?.idkpi ||
      item.chiTieuView?.idkpi ||
      anyItem.idKpi ||
      anyItem.idkpi ||
      anyItem.IDKPI ||
      item.tenKPI ||
      'unknown',
  );
}

export function getCopyDataTargetId(item: CopyDataTargetSetupItem): string {
  const anyItem = item as CopyDataTargetSetupItem & {
    Id?: string | null;
    ID?: string | null;
    chiTieuID?: string | null;
    destinationTargetID?: string | null;
    DestinationTargetID?: string | null;
    targetID?: string | null;
    TargetID?: string | null;
  };
  return String(
    item.chiTieu?.id ||
      item.chiTieuView?.chiTieuNam?.id ||
      anyItem.destinationTargetID ||
      anyItem.DestinationTargetID ||
      anyItem.targetID ||
      anyItem.TargetID ||
      item.id ||
      anyItem.Id ||
      anyItem.ID ||
      anyItem.chiTieuID ||
      '',
  ).trim();
}

export function getCopyDataTargetSourceYear(item: CopyDataTargetSetupItem): number | null {
  return toNumberOrNull(item.sourceYear) ?? toNumberOrNull(item.SourceYear);
}

export function getCopyDataTargetSourceTargetId(item: CopyDataTargetSetupItem): string | null {
  const anyItem = item as CopyDataTargetSetupItem & {
    sourceTargetId?: string | null;
    SourceTargetId?: string | null;
  };
  return (
    toStringOrNull(item.sourceTargetID) ??
    toStringOrNull(item.SourceTargetID) ??
    toStringOrNull(anyItem.sourceTargetId) ??
    toStringOrNull(anyItem.SourceTargetId)
  );
}

/** Khớp KPI đang mở modal với item trong target-setups. Không fallback item đầu. */
export function findCopyDataTargetSetupForKpi(
  setups: CopyDataTargetSetupItem[],
  current: InfoTarget,
): CopyDataTargetSetupItem | null {
  if (!setups.length || !current) return null;

  const currentId = String(current.chiTieu?.id || '').trim();
  const currentKpiId = String(
    current.chiTieu?.idkpi || current.chiTieuView?.idkpi || '',
  ).trim();
  const currentKey = getCopyDataTargetKpiKey(current);
  const currentName = String(current.tenKPI || current.chiTieuView?.tenkpi || '').trim();
  const normalizeName = (name: string) => name.replace(/\s+/g, ' ').trim().toLowerCase();
  const currentNameNorm = currentName ? normalizeName(currentName) : '';

  return (
    setups.find((item) => {
      const id = getCopyDataTargetId(item);
      return Boolean(currentId && id && id === currentId);
    }) ||
    setups.find((item) => {
      const kpiId = String(item.chiTieu?.idkpi || item.chiTieuView?.idkpi || '').trim();
      return Boolean(currentKpiId && kpiId && kpiId === currentKpiId);
    }) ||
    setups.find((item) => {
      const key = getCopyDataTargetKpiKey(item);
      return Boolean(currentKey && currentKey !== 'unknown' && key === currentKey);
    }) ||
    setups.find((item) => {
      const name = String(item.tenKPI || item.chiTieuView?.tenkpi || '').trim();
      return Boolean(currentNameNorm && name && normalizeName(name) === currentNameNorm);
    }) ||
    null
  );
}

export function groupCopyDataTargetsByKpi(
  items: CopyDataTargetSetupItem[],
): Map<string, CopyDataTargetSetupItem[]> {
  const groups = new Map<string, CopyDataTargetSetupItem[]>();
  items.forEach((item) => {
    const key = getCopyDataTargetKpiKey(item);
    const list = groups.get(key) || [];
    list.push(item);
    groups.set(key, list);
  });
  return groups;
}

const BASE_URL = '/v1/tenant';

const tenantApiConfig = (config: InternalAxiosRequestConfig) =>
  withTenantApiBase({
    ...config,
    authorization: true,
  } as InternalAxiosRequestConfig);

export const tenantApi = {
  copydataFromDashboard: (model: RequestCopyDataFromDashboard) =>
    request.post(`${BASE_URL}/copydata-from-dashboard/`, model, tenantApiConfig({} as InternalAxiosRequestConfig)),

  getCopyDataStatus: (payload: RequestCopyDataStatus) =>
    request.post(`${BASE_URL}/copydata-from-dashboard/status`, payload, tenantApiConfig({} as InternalAxiosRequestConfig)),

  getCopyDataTargetSetups: (params: GetCopyDataTargetSetupsParams) =>
    request.post(`${BASE_URL}/copydata/target-setups`, params, tenantApiConfig({} as InternalAxiosRequestConfig)),

  getTargetDetailHistory: (params: GetTargetDetailHistoryParams) =>
    request.post(`/v1/performance/gettargetdetailhistory`, params, {
      authorization: true,
      baseURL: ENVIRONMENT.API_HOST,
    } as InternalAxiosRequestConfig),
};
